// src/hooks/useDrawerSwipe.js
// Gestes natifs du drawer mobile : ouverture par swipe depuis le bord gauche,
// fermeture par swipe vers la gauche. Le drawer suit le doigt (drag-follow),
// et on ne commit l'ouverture/fermeture qu'au relâchement selon distance + vitesse.
import { useCallback, useEffect, useRef, useState } from "react";

const EDGE_ZONE = 28; // px depuis le bord gauche pour armer l'ouverture
const AXIS_LOCK = 10; // px parcourus avant de décider horizontal vs vertical
const COMMIT_RATIO = 0.4; // fraction de la largeur à dépasser pour valider
const FLING_V = 0.5; // px/ms — au-delà, le geste est validé quel que soit le trajet

export default function useDrawerSwipe({ open, onOpen, onClose, width, enabled = true }) {
  // offset = position en px du drawer (fermé = -width, ouvert = 0).
  // null quand aucun drag n'est en cours → l'anim CSS reprend la main.
  const [offset, setOffset] = useState(null);
  const g = useRef(null);

  const end = useCallback(() => {
    const s = g.current;
    g.current = null;
    if (!s || !s.axis) {
      setOffset(null);
      return;
    }

    const dt = Math.max(1, performance.now() - s.lastT);
    const v = (s.lastX - s.prevX) / dt; // px/ms, >0 = vers la droite
    const progress = (s.offset + width) / width; // 0 = fermé, 1 = ouvert

    let shouldOpen;
    if (Math.abs(v) > FLING_V) shouldOpen = v > 0;
    else shouldOpen = progress > COMMIT_RATIO;

    setOffset(null);
    if (shouldOpen) onOpen?.();
    else onClose?.();
  }, [width, onOpen, onClose]);

  useEffect(() => {
    if (!enabled) return undefined;

    const onStart = (e) => {
      if (e.touches.length !== 1) return;
      const t = e.touches[0];
      // Fermé : n'armer que depuis le bord gauche. Ouvert : n'importe où.
      if (!open && t.clientX > EDGE_ZONE) return;
      g.current = {
        startX: t.clientX,
        startY: t.clientY,
        prevX: t.clientX,
        lastX: t.clientX,
        lastT: performance.now(),
        axis: null,
        offset: open ? 0 : -width,
      };
    };

    const onMove = (e) => {
      const s = g.current;
      if (!s) return;
      const t = e.touches[0];
      const dx = t.clientX - s.startX;
      const dy = t.clientY - s.startY;

      if (!s.axis) {
        if (Math.abs(dx) < AXIS_LOCK && Math.abs(dy) < AXIS_LOCK) return;
        // Scroll vertical → on rend la main à la page.
        if (Math.abs(dy) > Math.abs(dx)) {
          g.current = null;
          return;
        }
        s.axis = "x";
      }

      // Le drawer suit le doigt, borné entre fermé et ouvert.
      const base = open ? 0 : -width;
      s.offset = Math.min(0, Math.max(-width, base + dx));
      s.prevX = s.lastX;
      s.lastX = t.clientX;
      s.lastT = performance.now();

      // Empêche le scroll de la page pendant le drag horizontal.
      if (e.cancelable) e.preventDefault();
      setOffset(s.offset);
    };

    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchmove", onMove, { passive: false });
    document.addEventListener("touchend", end, { passive: true });
    document.addEventListener("touchcancel", end, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", end);
      document.removeEventListener("touchcancel", end);
    };
  }, [enabled, open, width, end]);

  // Un changement d'état externe (clic sur le burger, navigation) annule tout drag résiduel.
  useEffect(() => {
    g.current = null;
    setOffset(null);
  }, [open]);

  return offset;
}
